import type { SelectItem } from '@vrplatform/components';
import type {
  accountAssignmentType_enum,
  party_enum,
} from '@vrplatform/graphql';
import { formatAssignmentType } from '@vrplatform/ui-common';
import { sortBy, toTitleCase } from '@vrplatform/utils';
import type { Account } from './useAccountsQuery';

type AccountClassification = NonNullable<
  NonNullable<Account['category']>['classification']
>;

type TransactionLineAccountOptionAccount = Pick<
  Account,
  'id' | 'name' | 'status' | 'type' | 'category' | 'assignments'
>;

type TransactionLinePropertyRuleAccount = Pick<
  Account,
  'category' | 'assignments'
>;

export type TransactionLineAccountClassificationOrder = Record<
  AccountClassification,
  number
>;

export type TransactionLineAccountType = 'deposit' | 'expense';

export const EXPENSE_TRANSACTION_LINE_ACCOUNT_CLASSIFICATION_ORDER: TransactionLineAccountClassificationOrder =
  {
    expense: 1,
    liability: 2,
    asset: 3,
    revenue: 4,
  };

export const DEPOSIT_TRANSACTION_LINE_ACCOUNT_CLASSIFICATION_ORDER: TransactionLineAccountClassificationOrder =
  {
    revenue: 1,
    asset: 2,
    liability: 3,
    expense: 4,
  };

const ALWAYS_DISABLED_ASSIGNMENTS: accountAssignmentType_enum[] = [
  'accountsPayable',
  'accountsReceivable',
];

export const TAXES_PAYABLE_CATEGORY_ID = '02052f13-0b10-4b82-8105-ddb8a11a668a';

const formatAssignedMessage = (assignments: accountAssignmentType_enum[]) => {
  const titles = assignments.map((assignment) =>
    formatAssignmentType(assignment)
  );

  return titles.length === 1
    ? `This account is assigned to ${titles[0]}`
    : `This account is assigned to ${titles.join(' & ')}`;
};

export const getTransactionLineAccountDisabledMessage = ({
  assignments,
  party,
  categoryId,
  transactionType,
  allowOwnerExpensePayoutDistribution = false,
}: {
  assignments: accountAssignmentType_enum[];
  party: party_enum | null | undefined;
  categoryId: string | null | undefined;
  transactionType: TransactionLineAccountType;
  allowOwnerExpensePayoutDistribution?: boolean;
}) => {
  const alwaysDisabledAssignments = ALWAYS_DISABLED_ASSIGNMENTS.filter(
    (assignment) => assignments.includes(assignment)
  );

  if (alwaysDisabledAssignments.length > 0) {
    return formatAssignedMessage(alwaysDisabledAssignments);
  }

  if (
    party === 'owners' &&
    assignments.includes('payout_distribution') &&
    !(transactionType === 'expense' && allowOwnerExpensePayoutDistribution)
  ) {
    return `${formatAssignmentType('payout_distribution')} account cannot be assigned on lines set to "Bill to owner"`;
  }

  if (party === 'manager' && categoryId === TAXES_PAYABLE_CATEGORY_ID) {
    return 'Taxes Payable - Lodging & Occupancy accounts cannot be assigned on lines set to "Bill to manager"';
  }

  return undefined;
};

export const isTransactionLinePropertyOptionalForAccount = (
  account: TransactionLinePropertyRuleAccount | null | undefined
) => {
  const assignments = (account?.assignments ?? []).filter(
    (assignment): assignment is accountAssignmentType_enum => !!assignment
  );

  return (
    account?.category?.id === TAXES_PAYABLE_CATEGORY_ID ||
    assignments.includes('accountsReceivable')
  );
};

const getTransactionLineAccountSortKey = ({
  account,
  classificationOrder,
}: {
  account: TransactionLineAccountOptionAccount;
  classificationOrder: TransactionLineAccountClassificationOrder;
}) => {
  const categoryOrder = account.category?.classification
    ? classificationOrder[account.category.classification]
    : Number.MAX_SAFE_INTEGER;
  const categoryName = (account.category?.name ?? '').toLowerCase();
  const name = (account.name ?? '').toLowerCase();

  return `${String(categoryOrder).padStart(10, '0')}:${categoryName}:${name}`;
};

export const getTransactionLineAccountOptions = (
  accounts: readonly TransactionLineAccountOptionAccount[],
  {
    party,
    classificationOrder,
    transactionType,
    allowOwnerExpensePayoutDistribution = false,
  }: {
    party: party_enum | null | undefined;
    classificationOrder: TransactionLineAccountClassificationOrder;
    transactionType: TransactionLineAccountType;
    allowOwnerExpensePayoutDistribution?: boolean;
  }
): SelectItem[] => {
  return sortBy(
    accounts.filter((account) => {
      const status = account.status ?? 'active';
      const type = account.type ?? 'ledger';

      return status === 'active' && type === 'ledger';
    }),
    (account) =>
      getTransactionLineAccountSortKey({
        account,
        classificationOrder,
      })
  ).map<SelectItem>((account) => {
    const assignments = (account.assignments ?? []).filter(
      (assignment): assignment is accountAssignmentType_enum => !!assignment
    );
    const disabled = getTransactionLineAccountDisabledMessage({
      assignments,
      party,
      categoryId: account.category?.id,
      transactionType,
      allowOwnerExpensePayoutDistribution,
    });

    return {
      value: account.id,
      label: account.name || 'No name',
      group: toTitleCase(account.category?.classification || '-'),
      disabled,
      description: disabled,
    };
  });
};
